import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useProductRule } from "./use-productRuleData";
import { useForeignShopRule } from "./use-foreignShopRuleData";
import { useActionRule } from "./use-actionRuleDate";
import RuleEngineBackend from "../../services/RuleEngineBackend";
import PPUtils from "../../../utils/PPUtils";

/**
 * @typedef {Object} OptionsState
 * @property {Object} static - Static hardcoded options
 * @property {Object} server - Server-fetched options
 * @property {Object} computed - Computed options from other contexts
 */

/**
 * @typedef {Object} ProductRuleOptions
 * @property {Object} languageAndChannel - Language and channel options
 * @property {string[]} languageAndChannel.language - Available languages
 * @property {string[]} languageAndChannel.channel - Available channels
 * @property {Object} stock - Stock options
 * @property {string[]} stock.stock - Available stock types
 * @property {Object} productAttribute - Product attribute options
 * @property {string[]} productAttribute.attribute - Available attributes
 * @property {string[]} productAttribute.operators - Available operators
 */

/**
 * @typedef {Object} ForeignShopRuleOptions
 * @property {Object} priceTracking - Price tracking options
 * @property {string[]} priceTracking.shops - Available shops
 * @property {Object} foreignShopstock - Foreign shop stock options
 * @property {Object[]|null} foreignShopstock.referencedStockRule - Referenced stock rules
 * @property {string[]} foreignShopstock.stock - Available stock types
 */

/**
 * @typedef {Object} ActionRuleOptions
 * @property {Object} priceChangeMargin - Price change margin options
 * @property {Object[]} priceChangeMargin.useCase - Available use cases
 * @property {Object[]} priceChangeMargin.priceChange - Price change types
 * @property {string[]} priceChangeMargin.margin - Available margin types
 * @property {Object} calculationChannelAndRulePrice - Calculation options
 * @property {string[]} calculationChannelAndRulePrice.channel - Available channels
 * @property {string[]} calculationChannelAndRulePrice.rule - Available rules
 * @property {Object} roundPrice - Round price options
 * @property {Object[]} roundPrice.type - Rounding types
 * @property {Object} changeInterval - Change interval options
 * @property {Object[]} changeInterval.interval - Available intervals
 * @property {string[]} changeInterval.weekdays - Available weekdays
 * @property {Object} priceChange - Price change options
 * @property {Object[]} priceChange.referencedStockRule - Referenced stock rules
 * @property {string[]} priceChange.ComparisonOption - Comparison options
 */

/**
 * @typedef {Object} OptionsContextValue
 * @property {OptionsState} options - Current options state
 * @property {() => Promise<void>} fetchServerOptions - Fetch all server options
 * @property {() => void} computeOptions - Compute all derived options
 * @property {() => ProductRuleOptions} getProductRuleOptions - Get product rule options
 * @property {() => ForeignShopRuleOptions} getForeignShopRuleOptions - Get foreign shop rule options
 * @property {() => ActionRuleOptions} getActionRuleOptions - Get action rule options
 */

export const OptionsContext = createContext();

export const OptionsContextProvider = ({ children }) => {
     const { StockHelper } = useProductRule();
     const { ForeignShopRuleHelper } = useForeignShopRule();
     const { ActionRuleHelper } = useActionRule();

     const [options, setOptions] = useState({
          static: {
               productRule: {
                    stock: {
                         stock: ["rot", "grün", "gelb"]
                    },
                    productAttribute: {
                         attribute: ["uvp", "ek"],
                         operators: ["<", "<=", "=", ">", ">="],
                         stringOperators: ["=", "IN", "CONTAINS"]
                    }
               },
               foreignShopRule: {
                    priceTracking: {
                         shops: ["google", "hoogle"]
                    },
                    foreignShopstock: {
                         stock: ["rot", "grün", "gelb"]
                    }
               },
               actionRule: {
                    priceChangeMargin: {
                         useCase: [
                              {id: 0, name: "Auf alle Produkte"},
                              {id: 1, name: "Nur, wenn aktuelle Marge kleiner"},
                              {id: 2, name: "Nur, wenn aktuelle Marge größer"}
                         ],
                         priceChange: [
                              {id: 0, name: "Formel"},
                              {id: 1, name: "Nettofaktor"},
                              {id: 2, name: "Bruttofaktor"}
                         ],
                         margin: ["Einkaufspreis", "uvp"]
                    },
                    roundPrice: {
                         type: [
                              {id: 0, name: "Immer aufrunden"},
                              {id: 1, name: "Immer abrunden"},
                              {id: 2, name: "Ab der Hälfte auf-, sonst abrunden"}
                         ]
                    },
                    changeInterval: {
                         interval: [
                              {id: 0, name: "Wöchentlich"},
                              {id: 1, name: "Monatlich"},
                              {id: 2, name: "Jährlich"}
                         ],
                         weekdays: [
                              "Montag", "Dienstag", "Mittwoch", "Donnerstag",
                              "Freitag", "Samstag", "Sonntag"
                         ]
                    },
                    priceChange: {
                         ComparisonOption: [
                              "günstigster Preis",
                              "zweitgünstigster Preis",
                              "teuerster Preis",
                              "Durchschnittspreis",
                              "Median",
                              "nächster Preis oberhalb des Mindestpreis (Excl. Preisangleichung)",
                              "nächster Preis oberhalb des Mindestpreis (Incl. Preisangleichung)"
                         ]
                    }
               }
          },               
          server: {
               productRule: {
                    languages: [],
                    channels: []
               },
               foreignShopRule: {
                    availableShops: [],
                    pricePortals: []
               },
               actionRule: {
                    existingRules: [],
                    availableChannels: [],
                    productAttributeNumericKeys: [],
                    productAttributeKeysWithType: []
               },
               categories: {
                    departments: [],
                    areas: [],
                    categories: [],
                    articles: [],
                    manufacturers: []
               }
          },
          computed: {
               productRule: {
                    filteredChannels: [],
                    filteredCategories: []
               },
               foreignShopRule: {
                    referencedStockRules: []
               },
               actionRule: {
                    availableRulesForChannel: []
               }
          }
     });

     /**
      * @returns {Promise<void>}
      */
     const fetchServerOptions = async () => {
          try {
               const request = PPUtils.getCustomerRequestContext();
               if (request == null) {
                    return;
               }
               const { token, customerId } = request;

               const languages = await RuleEngineBackend.getLanguages(token);

               let channels = await RuleEngineBackend.getChannels(customerId, token);
               if(channels && !Array.isArray(channels)){
                    channels = [channels];
               }

               let shops = await RuleEngineBackend.getShops(token);
               if(shops && !Array.isArray(shops)){
                    shops = [shops];
               }

               const productAttributeNumericKeys = await RuleEngineBackend.getProductAttributeNumericKeys(token);
               
               const productAttributeKeysWithType = await RuleEngineBackend.getProductAttributeKeysWithType(token, customerId);
               
               const topCategories = await RuleEngineBackend.getTopCategories(token, customerId);

               setOptions(prev => ({
                    ...prev,
                    server: {
                         ...prev.server,
                         productRule: {
                              ...prev.server.productRule,
                              languages: languages && Array.isArray(languages) ? languages : [],
                              channels: channels && Array.isArray(channels) ? channels.filter((channel) => !channel.hidden) : []
                         },
                         foreignShopRule: {
                              ...prev.server.foreignShopRule,
                              availableShops: shops && Array.isArray(shops) ? shops : []
                         },
                         actionRule: {
                              ...prev.server.actionRule,
                              productAttributeNumericKeys: productAttributeNumericKeys && Array.isArray(productAttributeNumericKeys) ? productAttributeNumericKeys : [],
                              productAttributeKeysWithType: productAttributeKeysWithType && Array.isArray(productAttributeKeysWithType) ? productAttributeKeysWithType : [],
                              availableChannels: channels && Array.isArray(channels) ? channels : []
                         },
                         categories: {
                              ...prev.server.categories,
                              departments: topCategories && Array.isArray(topCategories) ? 
                                   topCategories.map(cat => ({
                                        value: cat.id,
                                        label: cat.name,
                                        catShort: cat.catShort
                                   })) : []
                         }
                    }
               }));
          } catch (error) {
               console.error('Error fetching server options:', error);
          }
     };

     const computeOptions = () => {
          let referencedStockRules = [];
          
          if(StockHelper?.hasActiveStockRules()) {
               const activeStockRules = StockHelper.getStockRules();
               const stockColors = ["rot", "grün", "gelb"];
               
               referencedStockRules = activeStockRules.map(stockRule => {
                    const selectedColors = stockColors.filter((color, index) => stockRule.stock[index]);
                    return {
                         id: stockRule.id,
                         name: `Verfügbarkeit: ${selectedColors.join("-") || "Alle"}`,
                         stockTypes: stockRule.stock
                    };
               });
          }

          setOptions(prev => ({
               ...prev,
               computed: {
                    ...prev.computed,
                    foreignShopRule: {
                         ...prev.computed.foreignShopRule,
                         referencedStockRules: referencedStockRules
                    },
                    actionRule: {
                         ...prev.computed.actionRule,
                         referencedStockRules: referencedStockRules
                    }
               }
          }));
     };

     /**
      * @returns {ProductRuleOptions}
      */
     const getProductRuleOptions = () => ({
          languageAndChannel: {
               language: options.server.productRule.languages.length > 0 
                    ? options.server.productRule.languages 
                    : [], 
               channel: options.server.productRule.channels.length > 0 
                    ? options.server.productRule.channels 
                    : []
          },
          stock: options.static.productRule.stock,
          productAttribute: options.static.productRule.productAttribute
     });

     /**
      * @returns {ForeignShopRuleOptions}
      */
     const getForeignShopRuleOptions = () => ({
          priceTracking: {
               shops: options.server.foreignShopRule.availableShops.length > 0 
                    ? options.server.foreignShopRule.availableShops 
                    : []
          },
          foreignShopstock: {
               referencedStockRule: options.computed.foreignShopRule.referencedStockRules,
               stock: options.static.foreignShopRule.foreignShopstock.stock
          }
     });

     /**
      * @returns {ActionRuleOptions}
      */
     const getActionRuleOptions = () => ({
          priceChangeMargin: options.static.actionRule.priceChangeMargin,
          calculationChannelAndRulePrice: {
               channel: options.server.actionRule.availableChannels.length > 0 
                    ? options.server.actionRule.availableChannels 
                    : [{id: 1, channelName: "Onlineshop"}, {id: 2, channelName: "Offlineshop"}],
               rule: options.server.actionRule.existingRules
          },
          roundPrice: options.static.actionRule.roundPrice,
          changeInterval: options.static.actionRule.changeInterval,
          priceChange: {
               referencedStockRule: options.computed.actionRule.referencedStockRules || [],
               ComparisonOption: options.static.actionRule.priceChange.ComparisonOption
          },
          productAttributeNumericKeys: [
               { value: "Einkaufspreis", label: "Einkaufspreis" },
               ...options.server.actionRule.productAttributeNumericKeys
                    .filter((key) => key !== "Einkaufspreis")
                    .map((key) => ({ value: key, label: key })),
          ]
     });

     const getProductAttributeOptions = () => ({
          attributeKeys: options.server.actionRule.productAttributeKeysWithType && Array.isArray(options.server.actionRule.productAttributeKeysWithType) 
               ? options.server.actionRule.productAttributeKeysWithType
                    .filter((attr) => !(attr.attributeType === "string" && attr.attributeKey?.toLowerCase() === "tags"))
                    .map(attr => ({
                    value: attr.attributeKey,
                    label: attr.attributeType === "tag"
                         ? `${attr.attributeKey} (Tag)`
                         : attr.attributeKey,
                    type: attr.attributeType
               }))
               : [],
          operators: options.static.productRule.productAttribute.operators || [],
          stringOperators: options.static.productRule.productAttribute.stringOperators || ["=", "IN", "CONTAINS"]
     });

     const getCategoryOptions = () => ({
          departments: options.server.categories.departments,
          areas: options.server.categories.areas,
          categories: options.server.categories.categories,
          articles: options.server.categories.articles,
          manufacturers: options.server.categories.manufacturers
     });

     const fetchProductAttributeStringValues = useCallback(async (attributeKey) => {
          const request = PPUtils.getCustomerRequestContext();
          if (request == null) {
               return [];
          }
          const { token, customerId } = request;
          const stringValues = await RuleEngineBackend.getProductAttributeStringValues(token, customerId, attributeKey);
          
          if (stringValues && Array.isArray(stringValues)) {
               return stringValues.map(value => ({
                    value: value,
                    label: value
               }));
          }
          return [];
     }, []);

     const fetchProducers = useCallback(async () => {
          const request = PPUtils.getCustomerRequestContext();
          if (request == null) {
               return [];
          }
          const { token, customerId } = request;
          const producers = await RuleEngineBackend.getProducers(token, customerId);
          
          if (producers && Array.isArray(producers)) {
               return producers.map(producer => ({
                    value: producer,
                    label: producer
               }));
          }
          return [];
     }, []);

     const fetchOnlineShops = useCallback(async (language) => {
          const auth = PPUtils.getLogin();
          const token = auth.token;
          const shops = await RuleEngineBackend.getShopsByLanguage(token, language);

          if (!shops || !Array.isArray(shops)) {
               return [];
          }

          let filteredShops = shops;
          const configToUse = PPUtils.getEffectiveConfigShopsActivated(auth);
          if (configToUse) {
               const activatedIds = configToUse.split(',').map(id => parseInt(id.trim(), 10));
               filteredShops = shops.filter(shop => activatedIds.includes(shop.id));
          }
          
          return filteredShops.map(shop => ({
               value: shop.name,
               label: shop.lang ? `${shop.name} (${shop.lang})` : shop.name,
               id: shop.id,
               lang: shop.lang
          }));
     }, []);

     const fetchPriceTrackingShops = useCallback(async (language) => {
          const auth = PPUtils.getLogin();
          const shops = await RuleEngineBackend.getShopsByLanguage(auth.token, language);

          if (!Array.isArray(shops)) {
               return [];
          }

          const configToUse = PPUtils.getEffectiveConfigShopsActivated(auth);
          if (!configToUse) {
               return shops;
          }

          const activatedIds = configToUse.split(",").map((id) => parseInt(id.trim(), 10));
          return shops.filter((shop) => activatedIds.includes(shop.id));
     }, []);

     const getConditionOptions = useCallback(() => {
          return {
               shopAttributeOptions: [
                    { value: "HERSTELLER", label: "Hersteller", attributeType: "string" },
                    { value: "ONLINESHOP", label: "Onlineshop", attributeType: "string" },
                    { value: "PREIS", label: "Preis (ohne Versandkosten)", attributeType: "numeric" },
                    { value: "GESAMTPREIS", label: "Gesamtpreis (mit Versandkosten)", attributeType: "numeric" },
                    { value: "VERSANDKOSTEN", label: "Versandkosten", attributeType: "numeric" },
                    { value: "RATING_COUNT", label: "Anzahl Bewertungen", attributeType: "numeric" }
               ],
               importedAttributeKeys: options.server.actionRule.productAttributeKeysWithType && Array.isArray(options.server.actionRule.productAttributeKeysWithType) 
                    ? options.server.actionRule.productAttributeKeysWithType
                         .filter((attr) => !(attr.attributeType === "string" && attr.attributeKey?.toLowerCase() === "tags"))
                         .map(attr => ({
                         value: attr.attributeKey,
                         label: attr.attributeType === "tag"
                              ? `${attr.attributeKey} (Tag)`
                              : attr.attributeKey,
                         type: attr.attributeType
                    }))
                    : [],
               operators: (options.static.productRule.productAttribute.operators || []).map(operator => ({
                    value: operator,
                    label: operator
               })),
               stringOperators: (options.static.productRule.productAttribute.stringOperators || ["=", "IN", "CONTAINS"]).map(operator => ({
                    value: operator,
                    label: operator
               })),
               fetchProductAttributeStringValues,
               fetchProducers,
               fetchOnlineShops
          };
     }, [options.server.actionRule.productAttributeKeysWithType, options.static.productRule.productAttribute.operators, options.static.productRule.productAttribute.stringOperators, fetchProductAttributeStringValues, fetchProducers, fetchOnlineShops]);


     useEffect(() => {
          computeOptions();
     }, [StockHelper, ForeignShopRuleHelper, ActionRuleHelper]);

     const contextValue = {
          options,
          fetchServerOptions,
          computeOptions,
          getProductRuleOptions,
          getForeignShopRuleOptions,
          getActionRuleOptions,
          getProductAttributeOptions,
          getCategoryOptions,
          getConditionOptions,
          fetchProductAttributeStringValues,
          fetchProducers,
          fetchOnlineShops,
          fetchPriceTrackingShops,
     };

     return (
          <OptionsContext.Provider value={contextValue}>
               {children}
          </OptionsContext.Provider>
     );
};

/**
 * @returns {OptionsContextValue}
 */
export const useOptions = () => {
     const context = useContext(OptionsContext);
     if (!context) {
          throw new Error("useOptions must be used within OptionsContextProvider");
     }
     return context;
};
